// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
//
// Citizen developers asking IT to connect a system the org has not connected yet.
// It sits on the Integrations registry rather than the Grants sub-view: Grants is
// the inventory of who holds what against integrations that already exist, and this
// is a request for a new one. Approving records the decision and opens the ordinary
// catalog Register flow prefilled — it never connects anything on its own.
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DeclineConnectionRequestDialog } from '@/components/integrations/DeclineConnectionRequestDialog';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions } from '@/lib/polling';
import type { ConnectionRequestQueueItem } from '@greenlight/shared';

const QUEUE_KEY = ['integration-connection-requests', 'pending'];

// The contract's ceiling on one page (docs/16 §Pagination). The queue is a review
// surface, not a browsing one — at any plausible volume every pending request fits, so
// it reads one page rather than carrying a Load more. Beyond that the badge says "100+"
// and the copy points at the rest, instead of a count that quietly understates the
// backlog.
const PAGE_LIMIT = 100;

export interface ConnectionRequestsQueueProps {
  /** Opens the catalog Register dialog prefilled with this entry. */
  onRegister: (catalogKey: string) => void;
}

export function ConnectionRequestsQueue({
  onRegister,
}: ConnectionRequestsQueueProps): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const approve = api.integrations.approveConnectionRequest.useMutation();
  const deny = api.integrations.denyConnectionRequest.useMutation();
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [decliningItem, setDecliningItem] = React.useState<ConnectionRequestQueueItem | null>(null);

  const query = api.integrations.listConnectionRequests.useQuery(
    QUEUE_KEY,
    { query: { status: 'pending', limit: PAGE_LIMIT } },
    { ...liveQueryOptions, queryKey: QUEUE_KEY },
  );

  const page = query.data?.status === 200 ? query.data.body : undefined;
  const items: ConnectionRequestQueueItem[] = page?.items ?? [];
  const hasMore = page?.next_cursor != null;
  // A forbidden or broken queue must not read as an empty one, or the card would
  // vanish silently.
  const transientRefreshFailure =
    query.isError && isTransientQueryError(query.error) && page !== undefined;
  const failed =
    (query.isError && !transientRefreshFailure) ||
    (query.data !== undefined && query.data.status !== 200);

  // The card is absent, not empty, when nothing is waiting: an empty queue is the
  // normal state and a permanent "no requests" panel would just add noise to the
  // registry. A failed read still surfaces, so a broken queue is never mistaken for
  // an empty one.
  const showCard = !query.isLoading && (items.length > 0 || failed || transientRefreshFailure);
  if (!showCard && decliningItem === null) return null;

  async function approveRequest(item: ConnectionRequestQueueItem): Promise<void> {
    setBusyId(item.id);
    try {
      // An already-decided 409 rejects here, so IT is never walked into registering
      // against an approval that never saved.
      const res = await approve.mutateAsync({ params: { id: item.id }, body: {} });
      if (res.status !== 200) throw res;
      toast.success(`Approved — register ${item.display_name} to connect it.`);
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      // Only after the decision is recorded: opening the Register dialog on a
      // decision that failed to save would imply an approval that never happened.
      onRegister(item.catalog_key);
    } catch {
      toast.error('Could not record that decision. Reload the queue and try again.');
    } finally {
      setBusyId(null);
    }
  }

  async function declineRequest(decisionNote: string): Promise<void> {
    if (!decliningItem) return;
    const item = decliningItem;
    setBusyId(item.id);
    try {
      const res = await deny.mutateAsync({
        params: { id: item.id },
        body: { decision_note: decisionNote },
      });
      if (res.status !== 200) throw res;
      toast.success(`Declined the request for ${item.display_name}.`);
      setDecliningItem(null);
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    } catch {
      toast.error('Could not record that decision. Reload the queue and try again.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      {showCard && (
        <SectionCard
          headerBar
          className={items.length > 0 ? 'border-verdant ring-1 ring-verdant' : undefined}
          title="Requests to connect"
        >
          {transientRefreshFailure && (
            <div className="p-4">
              <RefreshWarning />
            </div>
          )}
          {failed ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertDescription>
                  Could not load connection requests. Reload the page to try again.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <li key={item.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
                  <IntegrationTile
                    slug={item.catalog_key}
                    displayName={item.display_name}
                    catalogKey={item.catalog_key}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">{item.display_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Requested by {item.requested_by.display_name ?? item.requested_by.email} ·{' '}
                      {new Date(item.requested_at).toLocaleDateString()}
                    </p>
                    {/* The reason is text an agent composed on a citizen developer's
                    behalf. React escapes it; it is never rendered as markup. */}
                    {item.reason ? (
                      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                        {item.reason}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      className="h-11 md:h-9"
                      disabled={busyId !== null}
                      onClick={() => void approveRequest(item)}
                    >
                      Approve &amp; register
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 md:h-9"
                      disabled={busyId !== null}
                      onClick={() => setDecliningItem(item)}
                    >
                      Decline
                    </Button>
                  </div>
                </li>
              ))}
              {hasMore ? (
                <li className="p-4 text-xs text-muted-foreground">
                  Showing the first {items.length}. Decide some to see the rest.
                </li>
              ) : null}
            </ul>
          )}
        </SectionCard>
      )}
      <DeclineConnectionRequestDialog
        item={decliningItem}
        open={decliningItem !== null}
        isPending={busyId !== null}
        onConfirm={(note) => void declineRequest(note)}
        onOpenChange={(o) => {
          if (!o) setDecliningItem(null);
        }}
      />
    </>
  );
}
